import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

export const themeColors = [
  { name: "Cyan", value: "cyan", primary: "#06b6d4", rgb: "6, 182, 212" },
  { name: "Purple", value: "purple", primary: "#a855f7", rgb: "168, 85, 247" },
  { name: "Green", value: "green", primary: "#22c55e", rgb: "34, 197, 94" },
  { name: "Orange", value: "orange", primary: "#f97316", rgb: "249, 115, 22" },
  { name: "Pink", value: "pink", primary: "#ec4899", rgb: "236, 72, 153" },
  { name: "Blue", value: "blue", primary: "#3b82f6", rgb: "59, 130, 246" },
  { name: "Red", value: "red", primary: "#ef4444", rgb: "239, 68, 68" },
  { name: "Yellow", value: "yellow", primary: "#eab308", rgb: "234, 179, 8" },
  { name: "Lime", value: "lime", primary: "#84cc16", rgb: "132, 204, 22" },
  { name: "Teal", value: "teal", primary: "#14b8a6", rgb: "20, 184, 166" },
  { name: "Indigo", value: "indigo", primary: "#6366f1", rgb: "99, 102, 241" },
  { name: "Rose", value: "rose", primary: "#f43f5e", rgb: "244, 63, 94" },
  { name: "Amber", value: "amber", primary: "#f59e0b", rgb: "245, 158, 11" },
  { name: "Violet", value: "violet", primary: "#8b5cf6", rgb: "139, 92, 246" },
];

export const buttonShapes = [
  { name: "Yuvarlak", value: "pill", radius: "9999px" },
  { name: "Varsayılan", value: "rounded", radius: "12px" },
  { name: "Keskin", value: "sharp", radius: "4px" },
];

export const bgEffects = [
  { name: "Parçacıklar", value: "particles" },
  { name: "Yıldızlar", value: "stars" },
  { name: "Gradient", value: "gradient" },
  { name: "Kapalı", value: "none" },
];

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('accent-color') || 'cyan';
    }
    return 'cyan';
  });

  const [buttonShape, setButtonShape] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('button-shape') || 'rounded';
    }
    return 'rounded';
  });

  const [bgEffect, setBgEffect] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('bg-effect') || 'particles';
    }
    return 'particles';
  });

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const themeColor = themeColors.find(t => t.value === currentTheme) || themeColors[0];
  const currentButtonShape = buttonShapes.find(b => b.value === buttonShape) || buttonShapes[1];

  useEffect(() => {
    localStorage.setItem('accent-color', currentTheme);
    document.documentElement.style.setProperty('--accent-color', themeColor.primary);
    document.documentElement.style.setProperty('--accent-rgb', themeColor.rgb);
  }, [currentTheme, themeColor]);

  useEffect(() => {
    localStorage.setItem('button-shape', buttonShape);
    document.documentElement.style.setProperty('--btn-radius', currentButtonShape.radius);
    document.body.setAttribute('data-btn-shape', buttonShape);
    // Inject targeted shape CSS - only buttons, links, inputs
    let style = document.getElementById('btn-shape-css');
    if (!style) {
      style = document.createElement('style');
      style.id = 'btn-shape-css';
      document.head.appendChild(style);
    }
    const r = currentButtonShape.radius;
    style.textContent = `
      button[class*="rounded"], 
      a[class*="rounded-xl"], 
      a[class*="rounded-2xl"],
      input[class*="rounded"],
      textarea[class*="rounded"] {
        border-radius: ${r} !important;
      }
    `;
  }, [buttonShape, currentButtonShape]);

  useEffect(() => {
    localStorage.setItem('bg-effect', bgEffect);
    document.body.setAttribute('data-bg-effect', bgEffect);
  }, [bgEffect]);

  useEffect(() => {
    const handleMouseMove = (e) => setCursorPosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1800);
    return () => clearTimeout(timer);
  }, []);

  const changeTheme = useCallback((themeName) => setCurrentTheme(themeName), []);
  const changeButtonShape = useCallback((shape) => setButtonShape(shape), []);
  const changeBgEffect = useCallback((effect) => setBgEffect(effect), []);
  const toggleSidePanel = useCallback(() => setIsSidePanelOpen(prev => !prev), []);

  return (
    <ThemeContext.Provider value={{
      currentTheme, themeColor, changeTheme, themeColors,
      buttonShape, currentButtonShape, changeButtonShape, buttonShapes,
      bgEffect, changeBgEffect, bgEffects,
      isSidePanelOpen, toggleSidePanel, setIsSidePanelOpen,
      cursorPosition, isLoading
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
